import { Grid, Typography } from '@mui/material';
import {
    ActionTypes,
    AgGridColumnDef,
    GridSettingsContext as IGridSettingsContext
} from '@tassat/common-definitions';
import { ApiConnectionForm } from '@tassat/forms';
import { APIConnectionRecord } from '@tassat/fred-definitions';
import { ApiPair as ApiPairIcon } from '@tassat/icon';
import React from 'react';
import { Route, Switch } from 'react-router-dom';

import { submitApiConnection } from 'Containers/admin/forms/helpers/apiConnectionFormFn';
import { useAppSelector } from 'Reducers/store/hooks';
import { getApiConnectionsArray } from 'Selectors/apiConnections';
import SideListLayout, { SidelistLayoutFormOptions } from 'SharedComponents/layouts/sideListLayout/';
import { GridSettingsContext } from 'SharedComponents/layouts/sideListLayout/gridSettingsContext';
import { SideListGridOptions } from 'SharedComponents/sideList';

import ApiConnectionDetails from './apiConnectionDetails';
import { getColumnDefs } from './apiConnectionGrid/apiConnectionsGridOptions';

import type { IApiConnectionsProps } from '.';

export const PAGE_TITLE = 'API';
const cellHeight = 60;

export function ApiConnections(props: IApiConnectionsProps) {
    const { classes } = props;
    const apiConnections = useAppSelector(getApiConnectionsArray);
    const [ addAPIConnectionModalOpen, setAddAPIConnectionModalOpen ] = React.useState<boolean>(false);
    const [ formType, setFormType ] = React.useState<ActionTypes>(ActionTypes.add);
    const [ editAPIConnection, setEditAPIConnection ] = React.useState<APIConnectionRecord>(null);
    const editApiConnection = (apiConnection) => {
        setEditAPIConnection(apiConnection);
        setFormType(ActionTypes.edit);
        setAddAPIConnectionModalOpen(true);
    };

    const layoutSettingsMemo: IGridSettingsContext = React.useMemo(() => {
        return {
            pageTitle: PAGE_TITLE,
            title: 'All API Connections',
            // The below are no longer taken from the Context in Side List
            columnDefs: undefined,
            options: { cellHeight: 0 }
        };
    }, []);

    const columnDefs: AgGridColumnDef[] = React.useMemo(() => getColumnDefs(cellHeight), []);

    const gridOptions: SideListGridOptions = React.useMemo(() => ({
        idField: 'APIConnectionId',
        cellHeight: cellHeight,
        searchByColumn: 'APIConnectionName'
    }), []);

    const formOptions: SidelistLayoutFormOptions = React.useMemo(() => ({
        formTitle: `${ActionTypes.edit == formType ? 'Edit' : 'New'} API Connection`,
        Form: ApiConnectionForm,
        formProps: {
            apiConnection: editAPIConnection,
            actionType: formType,
            modalOpen: addAPIConnectionModalOpen,
            apiConnections: apiConnections,
            longField: false,
            onClose: () => {
                setEditAPIConnection(null);
                setFormType(ActionTypes.add);
                setAddAPIConnectionModalOpen(false);
            },
            submitApiConnection: submitApiConnection
        }
    }), [ addAPIConnectionModalOpen, apiConnections, editAPIConnection, formType ]);

    return (
        <GridSettingsContext.Provider value={layoutSettingsMemo}>
            <SideListLayout
                columnDefs={columnDefs}
                formOptions={formOptions}
                gridOptions={gridOptions}
                rowData={apiConnections}
            >
                <Grid
                    container
                    direction='column'
                    item={true}
                    spacing={0}
                    xs={12}
                >
                    <Switch>
                        <Route
                            exact
                            path='/admin/apiConnections/:apiConnectionId'
                            render={props => (
                                <ApiConnectionDetails
                                    {...props}
                                    editApiConnection={editApiConnection}
                                />
                            )}
                        />
                        <Route
                            exact
                            path='/admin/apiConnections'
                            render={props => (
                                <Grid
                                    alignItems='center'
                                    className={classes.noSelectedConnection}
                                    container
                                    direction='column'
                                    item={true}
                                    justifyContent='center'
                                    spacing={0}
                                    wrap='nowrap'
                                >
                                    <ApiPairIcon className={classes.apiIcon} />
                                    <Typography variant='body1'>Please select an API Connection</Typography>
                                </Grid>
                            )}
                        />
                    </Switch>
                </Grid>
            </SideListLayout>
        </GridSettingsContext.Provider>
    );
}

export default ApiConnections;